import React from 'react';

/**
 * AppointmentSchedulerModal Component
 * Modal for creating new appointments with date, time, service, and location
 * 
 * Props:
 * - isOpen: Boolean to show/hide modal
 * - title: Modal title (e.g., "Schedule New Appointment" or "Edit Appointment")
 * - formData: Object containing form state (date, start_time, end_time, service, description, location)
 * - onFormChange: Callback function to update form data
 * - onSubmit: Callback function when form is submitted
 * - onClose: Callback function to close modal
 * - availableServices: Array of available service names
 * - isLoading: Boolean to show loading state during submission
 */
export default function AppointmentSchedulerModal({
    isOpen,
    title = "Schedule New Appointment",
    formData,
    onFormChange,
    onSubmit,
    onClose,
    availableServices = [],
    isLoading = false
}) {
    if (!isOpen) {
        return null;
    }

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        onFormChange({
            ...formData,
            [name]: value
        });
    };

    const handleCheckboxChange = (serviceName) => {
        const currentServices = formData.services || [];
        let updatedServices;
        
        if (currentServices.includes(serviceName)) {
            // Remove if already selected
            updatedServices = currentServices.filter(s => s !== serviceName);
        } else {
            // Add if not selected
            updatedServices = [...currentServices, serviceName];
        }
        
        onFormChange({
            ...formData,
            services: updatedServices
        });
    };

    const handleTextareaChange = (e) => {
        const { name, value } = e.target;
        onFormChange({
            ...formData,
            [name]: value
        });
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>{title}</h2>
                    <button 
                        className="modal-close" 
                        onClick={onClose} 
                        aria-label="Close modal"
                        disabled={isLoading}
                    >
                        ×
                    </button>
                </div>
                
                <form onSubmit={onSubmit} className="modal-form">
                    {/* Date Field */}
                    <div className="form-group">
                        <label htmlFor="scheduler-date">Date</label>
                        <input
                            type="date"
                            id="scheduler-date"
                            name="date"
                            value={formData.date}
                            onChange={handleInputChange}
                            className="form-input"
                            min="2000-01-01"
                            max="2099-12-31"
                            required
                            disabled={isLoading}
                        />
                    </div>

                    {/* Start Time Field */}
                    <div className="form-group">
                        <label htmlFor="scheduler-start-time">Start Time</label>
                        <input
                            type="time"
                            id="scheduler-start-time"
                            name="start_time"
                            value={formData.start_time}
                            onChange={handleInputChange}
                            className="form-input"
                            required
                            disabled={isLoading}
                        />
                    </div>

                    {/* End Time Field */}
                    <div className="form-group">
                        <label htmlFor="scheduler-end-time">End Time</label>
                        <input
                            type="time"
                            id="scheduler-end-time"
                            name="end_time"
                            value={formData.end_time}
                            onChange={handleInputChange}
                            className="form-input"
                            required
                            disabled={isLoading}
                        />
                    </div>

                    {/* Services Field - Checkboxes */}
                    <div className="form-group">
                        <label>Services</label>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '8px' }}>
                            {availableServices.length > 0 ? (
                                availableServices.map(service => (
                                    <label key={service} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                        <input
                                            type="checkbox"
                                            checked={(formData.services || []).includes(service)}
                                            onChange={() => handleCheckboxChange(service)}
                                            disabled={isLoading}
                                            style={{ cursor: 'pointer' }}
                                        />
                                        <span>{service}</span>
                                    </label>
                                ))
                            ) : (
                                <p style={{ color: '#999', fontSize: '14px' }}>No services available</p>
                            )}
                        </div>
                    </div>

                    {/* Location Field */}
                    <div className="form-group">
                        <label htmlFor="scheduler-location">Location</label>
                        <input
                            type="text"
                            id="scheduler-location"
                            name="location"
                            value={formData.location}
                            onChange={handleInputChange}
                            className="form-input"
                            required
                            placeholder="Enter appointment location"
                            disabled={isLoading}
                        />
                    </div>

                    {/* Description Field */}
                    <div className="form-group">
                        <label htmlFor="scheduler-description">Description</label>
                        <textarea
                            id="scheduler-description"
                            name="description"
                            value={formData.description}
                            onChange={handleTextareaChange}
                            className="form-textarea"
                            placeholder="Enter appointment description and specific services"
                            disabled={isLoading}
                        />
                    </div>

                    {/* Form Actions */}
                    <div className="modal-actions">
                        <button 
                            type="button" 
                            className="modal-button-cancel" 
                            onClick={onClose}
                            disabled={isLoading}
                        >
                            Cancel
                        </button>
                        <button 
                            type="submit" 
                            className="modal-button-submit"
                            disabled={isLoading}
                        >
                            {isLoading ? 'Submitting...' : 'Submit'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
